/**
 * BackgroundAnalyserReconnector
 *
 * App-level headless component that reconnects the analyser WebSocket after
 * a page reload while a Quality Intelligence generation was in-flight.
 *
 * The analyser stream service writes `analyser:inflight:<conversationId>` to
 * localStorage on every chunk. On mount here we scan for those entries and,
 * if one is live (< 30 min old), call setSession() + connect() so the
 * service's WS_OPEN handler can re-subscribe from the last received sequence.
 *
 * Rendered once in App.js alongside BackgroundGenerationReconnector.
 */

import { useEffect, useRef } from 'react';
import { analyzerStreamService } from '../workers/ChatStreamService';
import logger from '../services/logging/logger';

const INFLIGHT_PREFIX = 'analyser:inflight:';
const INFLIGHT_TTL_MS = 30 * 60 * 1000;

const BackgroundAnalyserReconnector = () => {
  const hasInit = useRef(false);

  useEffect(() => {
    if (hasInit.current) return;
    hasInit.current = true;

    let entry = null;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(INFLIGHT_PREFIX)) continue;
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (Date.now() - parsed.updatedAt > INFLIGHT_TTL_MS) {
          logger.info('BackgroundAnalyserReconnector: stale in-flight entry — skipping', { key });
          continue;
        }
        entry = parsed;
        break;
      }
    } catch (e) {
      logger.warn('BackgroundAnalyserReconnector: could not read localStorage', e);
    }

    if (!entry) return;

    logger.info('BackgroundAnalyserReconnector: reconnecting analyser for in-flight chat', {
      chatId: entry.chatId,
      conversationId: entry.conversationId,
      lastSequence: entry.lastSequence,
    });

    analyzerStreamService.setSession({
      userId: entry.userId,
      conversationId: entry.conversationId,
      tenantId: entry.tenantId,
    });
    // No cleanup disconnect — this component lives for the app session (mounted in
    // App.js, never unmounts). The one-time connect() keeps the service alive while
    // the background chat streams; _clearInflight() removes the localStorage entry
    // on success, so a fresh page load will not reconnect unnecessarily.
    analyzerStreamService.connect();
  }, []);

  return null;
};

export default BackgroundAnalyserReconnector;
